import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { transactionsApi } from '../../../services/financeApi';
import { useLocationNames, journalLocations } from '../../../utils/locationNames';

const money = (n) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

/**
 * Shows the double entries (GL debit/credit lines) posted for a document.
 * documentType: 'Bill' | 'BillPayment' | 'NormalPayment' | 'JournalEntry'
 */
const DoubleEntryPanel = ({ documentType, documentId }) => {
  const [state, setState] = useState({ loading: true, error: null, transactions: [] });
  const locationNames = useLocationNames();

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, transactions: [] });
    transactionsApi.getTransactionsBySource(documentType, documentId)
      .then(res => { if (!cancelled) setState({ loading: false, error: null, transactions: res.data?.data || [] }); })
      .catch(err => {
        if (!cancelled) setState({ loading: false, error: err.response?.data?.message || 'Could not load double entries', transactions: [] });
      });
    return () => { cancelled = true; };
  }, [documentType, documentId]);

  if (state.loading) {
    return <p className="text-xs text-gray-500 py-2">Loading double entries…</p>;
  }
  if (state.error) {
    return <p className="text-xs text-red-600 py-2">{state.error}</p>;
  }
  if (state.transactions.length === 0) {
    return (
      <p className="text-xs text-gray-500 py-2">
        No double entries yet — they are posted when the document is approved/posted.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {state.transactions.map(txn => {
        const debit = txn.lines.filter(l => l.lineType === 'DEBIT').reduce((s, l) => s + (l.amount || 0), 0);
        const credit = txn.lines.filter(l => l.lineType === 'CREDIT').reduce((s, l) => s + (l.amount || 0), 0);
        const balanced = Math.abs(debit - credit) < 0.005;
        return (
          <div key={txn._id} className="border border-gray-200 rounded-md overflow-hidden bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-200">
              <span className="text-xs font-semibold text-gray-800">
                {txn.docCode}
                <span className="ml-2 font-normal text-gray-500">{(txn.transactionType || '').replace(/_/g, ' ')}</span>
              </span>
              <span className="text-[11px] text-gray-500">
                {/* location(s) come from the lines — one journal can cover several */}
                {journalLocations(txn, locationNames).join(', ') || 'No location'} · {fmtDate(txn.transactionDate)}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-1.5 text-left font-semibold">Ledger Account</th>
                    <th className="px-3 py-1.5 text-left font-semibold">Location</th>
                    <th className="px-3 py-1.5 text-left font-semibold">Cost Center</th>
                    <th className="px-3 py-1.5 text-left font-semibold">Description</th>
                    <th className="px-3 py-1.5 text-right font-semibold">Debit (Rs)</th>
                    <th className="px-3 py-1.5 text-right font-semibold">Credit (Rs)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {txn.lines.map(line => (
                    <tr key={line._id}>
                      <td className="px-3 py-1.5 text-gray-800 whitespace-nowrap">
                        <span className={line.lineType === 'CREDIT' ? 'pl-4' : ''}>
                          <span className="font-mono text-gray-500 mr-1">{line.ledgerAccount?.code}</span>
                          {line.ledgerAccount?.name || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap">{line.location ? (locationNames[String(line.location)] || '…') : '—'}</td>
                      <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap" title={line.costCenter?.name || ''}>{line.costCenter?.code || '—'}</td>
                      <td className="px-3 py-1.5 text-gray-600">{line.description || '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-gray-900">
                        {line.lineType === 'DEBIT' ? money(line.amount) : ''}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-gray-900">
                        {line.lineType === 'CREDIT' ? money(line.amount) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-3 py-1.5 text-gray-700" colSpan={4}>
                      Total
                      <span className={`ml-2 text-[10px] font-medium ${balanced ? 'text-green-700' : 'text-red-600'}`}>
                        {balanced ? '✓ Balanced' : '⚠ Not balanced'}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-gray-900">{money(debit)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-gray-900">{money(credit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Modal wrapper — used from card views, where there is no room to expand inline.
 *  Rendered into document.body so card hover transforms/overflow can't clip it. */
export const DoubleEntryModal = ({ documentType, documentId, title, onClose }) => createPortal(
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
    <div className="w-full max-w-4xl bg-white rounded-lg shadow-xl" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Double Entries{title ? ` — ${title}` : ''}</h3>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none" aria-label="Close">×</button>
      </div>
      <div className="p-4 max-h-[70vh] overflow-y-auto">
        <DoubleEntryPanel documentType={documentType} documentId={documentId} />
      </div>
    </div>
  </div>,
  document.body
);

/** The "+" / "−" toggle button used in list rows and card actions */
export const DoubleEntryToggleButton = ({ open, onClick, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    title={open ? 'Hide double entries' : 'Show double entries'}
    aria-label={open ? 'Hide double entries' : 'Show double entries'}
    className={`inline-flex items-center justify-center w-5 h-5 rounded border text-xs font-bold leading-none transition-colors ${
      open ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-500 hover:border-indigo-400 hover:text-indigo-600'
    } ${className}`}
  >
    {open ? '−' : '+'}
  </button>
);

export default DoubleEntryPanel;
